/**
 * BlockSubscriptionModal — «Закрыть доступ» решением Skrezo (не из-за
 * оплаты): клиника уходит в режим чтения со статусом «Заблокирована».
 * Причина попадает в историю подписки. Снимается отметкой оплаты.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {TriangleAlert} from 'lucide-react';

import {blockSubscription} from '@services/clients';
import {keys} from '@queries/keys';
import {Field} from '@components/FormKit';

import {ModalShell} from '../ModalShell';

type Props = {
  open: boolean;
  clientId: string;
  clientName: string;
  onClose: () => void;
  onSaved: () => void;
};

export const BlockSubscriptionModal = ({open, clientId, clientName, onClose, onSaved}: Props) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setReason('');
  }

  const mutation = useMutation({
    mutationFn: blockSubscription,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      void queryClient.invalidateQueries({queryKey: keys.dashboard.all});
      onSaved();
    },
  });

  const handleSubmit = () => mutation.mutate({id: clientId, reason});

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Закрыть доступ"
      subtitle={
        <>
          <span className="font-semibold text-foreground">«{clientName}»</span> перейдёт в режим
          чтения: запись и оплаты закроются, данные останутся на месте.
        </>
      }
      submitLabel="Закрыть доступ"
      onSubmit={handleSubmit}
      submitDisabled={reason.trim() === ''}
      pending={mutation.isPending}
      error={mutation.isError ? 'Не удалось закрыть доступ. Попробуйте ещё раз' : null}
      danger
    >
      <div className="flex gap-2.5 rounded-[11px] border border-destructive/25 bg-destructive/5 p-[11px_13px]">
        <TriangleAlert className="mt-px size-[17px] shrink-0 text-destructive" strokeWidth={1.9} />
        <div className="text-xs leading-[1.45] text-skr-ink2">
          Действует сразу: врачи и администраторы увидят кабинет только для чтения. Вернуть доступ —
          «Отметить оплату».
        </div>
      </div>
      <Field label="Причина (попадёт в историю подписки)">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="Например: нарушение условий, просьба владельца"
          rows={3}
          className="w-full resize-none rounded-[10px] border border-input bg-card p-[11px_13px] text-sm leading-[1.5] outline-none transition focus:border-primary focus:ring-[3px] focus:ring-primary/12"
        />
      </Field>
    </ModalShell>
  );
};
